import { Injectable } from '@angular/core';
import { HttpHandler, HttpRequest, HttpEvent, HttpInterceptor, HttpResponse } from '@angular/common/http';
import { Observable, of, from } from 'rxjs';
import { LoggerService } from '../logger/logger.service';
import { map, catchError } from 'rxjs/operators';
import { InterceptorMessageService } from './interceptor.message.service';
import { HttpCacheService } from '../cache/http-cache-service';
import { OAuthService } from 'angular-oauth2-oidc';
import { CookieStateService } from 'src/app/shared/cookie/cookie-state-service';
import { AppConfigService } from 'src/app/core/app-config/app-config.service';
import { AuthConfigService } from '../security/auth-config.service';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {

  constructor(
    private oauthService: OAuthService,
    private loggerService: LoggerService,
    private interceptorMsgService: InterceptorMessageService,
    private cacheService: HttpCacheService,
    private cookieStateService: CookieStateService,
    private authConfigService: AuthConfigService,
    private appConfigService: AppConfigService) {
  }

  intercept(request: HttpRequest<void>, next: HttpHandler): Observable<HttpEvent<void>> {
    this.loggerService.log('request', request);

    if (![this.getBaseUrl(this.appConfigService.configuration?.apiConfig.babelServiceBaseUrl)]
        .includes(this.getBaseUrl(request.url))) {
      request = request.clone({
        setHeaders: {
          Authorization: 'Bearer ' + this.oauthService.getAccessToken()
        }
      });
    }

    const impersonatedData = this.cookieStateService.getState();
    if(request.url && this.authConfigService.configuration && !request.url.includes(this.authConfigService.configuration.issuer)){
      if (impersonatedData && impersonatedData.isImpersonated) {
        request = request.clone({
          setHeaders: {
            'X-Impersonate': impersonatedData.impersonatedUserId
          }
        });
      }
    }


    // Don't cache if it's not a GET request
    if (request.method !== 'GET') {
      if (!request.headers.has('no-loading')) {
        this.sendLoadingValue(true);
      }
      return from(next.handle(request).pipe(
        map((event: HttpEvent<any>) => {
          if (event instanceof HttpResponse) {
            this.sendLoadingValue(false);
            this.loggerService.log('response', event);
          }
          return event;
        }),
        catchError((errorResp) => {
          this.handleApiErrors(errorResp);
          this.sendLoadingValue(false);
          throw errorResp;
        })
      ).toPromise());
    }

    return from(this.handle(request, next));
  }

  async handle(request: HttpRequest<any>, next: HttpHandler): Promise<HttpEvent<void>> {
    if (request.headers.get('cache-response')) {
      const cachedResponse = await this.cacheService.getFromCache(request).toPromise();
      // Checked if there is cached data for this URI
      if (cachedResponse) {
        this.loggerService.log('cache', cachedResponse);
        return (cachedResponse instanceof Observable) ? cachedResponse : of(cachedResponse.clone()).toPromise();
      }
    }

    if (!request.headers.has('no-loading')) {
      this.sendLoadingValue(true);
    }
    return next.handle(request).pipe(
      map((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse) {
          if (request.headers.get('cache-response')) {
            // Cache Response if header contains cache-response
            this.cacheService.addToCache(request, event);
          }
          this.sendLoadingValue(false);
          this.loggerService.log('response', event);
        }
        return event;
      }),
      catchError((errorResp) => {
        this.handleApiErrors(errorResp);
        this.sendLoadingValue(false);
        throw errorResp;
      })
    ).toPromise();
  }

  handleApiErrors(errorResponse) {
    if (errorResponse.status === 0) {
      this.interceptorMsgService.generalError.next(true);
      this.interceptorMsgService.apiNetworkError.next(false);
    }
    if (errorResponse.status === 404) {
      this.sendLoadingValue(false);
      this.interceptorMsgService.api404Error.next(false);
    }
    if (errorResponse.status === 500 && !errorResponse.url.includes('processShipment') && !errorResponse.url.includes('getShipmentEstimates')) {
      this.interceptorMsgService.generalError.next(true);
      this.sendLoadingValue(false);
    }
  }

  sendLoadingValue(value: boolean) {
    this.interceptorMsgService.isLoading.next(value);
  }

  private getBaseUrl(url: string): string {
    const urlObj = new URL(url, window.location.origin);
    return `${urlObj.protocol}//${urlObj.host}`;
  }
}
